import { useRef, useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  AppBar,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@material-ui/core";
import ChevronRightIcon from "src/icons/ChevronRight";
import FormikForm, {
  FormikStep,
} from "src/components/ui-components/FormikForm";
import SelectField from "src/components/ui-components/SelectField";
import InputField from "src/components/ui-components/InputField";
import React from "react";
import RequestBody from "./RequestBody";
import Query from "./Query";
import ApiAuth from "./ApiAuth";
import CustomeSkeleton from "src/components/skeleton/Skeleton";
import AlertMessage from "src/components/ui-components/AlertMessage";
import * as Yup from "yup";
import {
  getWebMethodsByKey,
  getContentTypeMaster,
  getWebMethodVerbMaster,
  getRequestBodyDataTypeMaster,
  getWebMethodAuthTypeMaster,
  getFieldDataTypeMaster,
  getFieldDataTypeFormatMaster,
  executeWebMethod,
  insertUpdateWebMethods,
} from "./ConnectionService";

interface ParamsType {
  webMethodKey: string;
}

const webMethodsOAuth2RequestParams = {
  paramsKey: null,
  paramsValue: null,
};

const defaultForm = {
  webMethodKey: null,
  webMethodName: "",
  webMethodURL: "",
  webMethodAuthTypeKey: "",
  webMethodVerbKey: "",
  contentTypeKey: null,
  requestBodyDataTypeKey: null,
  requestBody: null,
  headers: null,
  responseBody: "",
  parameterSeparator: null,
  authURL: null,
  headerKey: null,
  oAuthHeaders: null,
  oAuthContentType: null,
  oAuthRequestBodyDataType: null,
  authTokenField: null,
  oAuthWebmethodVerbKey: null,
  oAuthRequestBody: null,
  webMethodsRequestParams: [],
  webMethodsOAuth2RequestParams: [],
  // webMethodsRequestParams: [webMethodsRequestParams],
  // webMethodsOAuth2RequestParams: [webMethodsOAuth2RequestParams],
  oAuth2ResponseFields: [],
};

const WebMethodsCreateEdit = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isTestButtonLoading, setIsTestButtonLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const { webMethodKey } = (location.state as ParamsType) || { webMethodKey: null };
  const [webMethodVerbMaster, setWebMethodVerbMaster] = useState([]);
  const [contentTypeMaster, setContentTypeMaster] = useState([]);
  const [requestBodyDataTypeMaster, setRequestBodyDataTypeMaster] = useState([]);
  const [webMethodAuthTypeMaster, setWebMethodAuthTypeMaster] = useState([]);
  const [fieldDataTypeMaster, setFieldDataTypeMaster] = useState([]);
  const [fieldDataTypeFormatMaster, setFieldDataTypeFormatMaster] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isAuthTabDisabled, setIsAuthTabDisabled] = useState(false);
  const [isQueryTabDisabled, setIsQueryTabDisabled] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const fieldArrayRef = useRef(null);

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: webMethodKey && true },
      });
    }
  };

  const submitForm = (e) => {
    e.preventDefault();

    if (formRef && formRef.current && !formRef.current.isSubmitting) {
      if (formRef.current.values.isEdit) {
        let hasEmptyFieldType = false;

        formRef.current.values.oAuth2ResponseFields.forEach((auth, idx) => {
          if (!auth.fieldType) {
            hasEmptyFieldType = true;
            formRef.current.setFieldTouched(
              `oAuth2ResponseFields[${idx}].fieldType`,
              true,
              true
            );
          }
        });

        if (!hasEmptyFieldType) {
          handleOpenDialog();
        }
      } else {
        formRef.current.submitForm();
      }
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialogAndSubmit = () => {
    setOpenDialog(false);
    formRef.current.submitForm();
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const useStyles = makeStyles((theme) => ({
    selectedTab: {
      backgroundColor: "transparent !important",
    },
  }));

  const classes = useStyles();

  const fetchWebMethodsData = async () => {
    setIsFetching(true);
    await getWebMethodsByKey(webMethodKey)
      .then((res) => {
        const updatedRes = {
          webMethodKey: res.webMethodKey,
          webMethodName: res.webMethodName,
          webMethodURL: res.webMethodURL,
          webMethodAuthTypeKey: res.webMethodAuthTypeKey,
          webMethodVerbKey: res.webMethodVerbKey,
          contentTypeKey: res.contentTypeKey,
          requestBodyDataTypeKey: res.requestBodyDataTypeKey,
          requestBody: res.requestBody,
          headers: res.headers,
          parameterSeparator: res.parameterSeparator,
          authURL: res.authURL,
          headerKey: res.headerKey,
          oAuthHeaders: res.oAuthHeaders,
          oAuthContentType: res.oAuthContentType,
          oAuthRequestBodyDataType: res.oAuthRequestBodyDataType,
          authTokenField: res.authTokenField,
          oAuthWebmethodVerbKey: res.oAuthWebmethodVerbKey,
          oAuthRequestBody: res.oAuthRequestBody,
          webMethodsRequestParams: res.webMethodsRequestParams || [],
          webMethodsOAuth2RequestParams:
            res.webMethodsOAuth2RequestParams || [],
          responseBody:
            (res.responseBody && res.responseBody.trim() !== '' && JSON.stringify(JSON.parse(res.responseBody), null, 2)) || "",
          oAuth2ResponseFields: res.oAuth2ResponseFields.map((field) => ({
            fieldName: field.fieldName,
            displayName: field.displayName,
            fieldType: field.fieldType,
            format: field.format,
            save: true,
          })),
        };
        resetFormData(updatedRes);
        setIsFetching(false);
      })
      .catch(() => {
        setIsFetching(false);
      });
  };

  useEffect(() => {
    setIsAuthTabDisabled(
      webMethodAuthTypeMaster?.find(
        (x) =>
          x.authenticationTypeMasterKey ===
          formRef?.current?.values?.webMethodAuthTypeKey
      )?.authenticationType !== "OAuth 2.0"
    );
  }, [formRef?.current?.values?.webMethodAuthTypeKey, webMethodAuthTypeMaster]);

  const getContentTypeMasterData = async () => {
    try {
      getContentTypeMaster().then((res) => {
        setContentTypeMaster(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const getWebMethodVerbMasterData = async () => {
    try {
      getWebMethodVerbMaster().then((res) => {
        setWebMethodVerbMaster(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const getRequestBodyDataTypeMasterData = async () => {
    try {
      getRequestBodyDataTypeMaster().then((res) => {
        setRequestBodyDataTypeMaster(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const getWebMethodAuthTypeMasterrData = async () => {
    try {
      getWebMethodAuthTypeMaster().then((res) => {
        setWebMethodAuthTypeMaster(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const getFieldDataTypeMasterData = async () => {
    try {
      getFieldDataTypeMaster().then((res) => {
        setFieldDataTypeMaster(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const getFieldDataTypeFormatMasterData = async () => {
    try {
      getFieldDataTypeFormatMaster().then((res) => {
        setFieldDataTypeFormatMaster(res);
      });
    } catch (err) {
      AlertMessage({ type: "error", msg: err.title });
    }
  };

  const authResponseData = (responseJson, parentKey = "") => {
    let fields = [];
    for (const key in responseJson) {
      if (responseJson.hasOwnProperty(key)) {
        const value = responseJson[key];
        const fieldName = parentKey ? `${parentKey}.${key}` : "";

        if (typeof value === "object" && value !== null) {
          // If value is an object and not null, recursively call authResponseData
          fields = fields.concat(authResponseData(value, fieldName));
        } else {
          // If value is not an object, add it to fields array
          fields.push({
            fieldName: parentKey ? fieldName : key,
            displayName: "",
            fieldType: null,
            format: null,
            save: true,
          });
        }
      }
    }
    return fields;
  };

  const handleTestButtonClick = async () => {
    try {
      setIsTestButtonLoading(true);
      const formData = formRef.current.values;

      function ValidateField(fieldName: string) {
        formRef.current.validateField(fieldName);
        formRef.current.setFieldTouched(fieldName, true);
      }
      var validateFieldList = [
        "webMethodURL",
        "webMethodAuthTypeKey",
        "webMethodVerbKey",
        "contentTypeKey",
        "requestBodyDataTypeKey",
        "requestBody",
        "webMethodsRequestParams"
      ];
      validateFieldList.map((field) => ValidateField(field));
      const hasErrors = validateFieldList.map((fieldName) => {
        return typeof formRef?.current?.errors[fieldName] === "string";
      });

      if (hasErrors.some((x) => x === true)) {
        setIsTestButtonLoading(false);
        return;
      }

      const oAuthValidateFieldList = [
        "oAuthContentType",
        "oAuthRequestBodyDataType",
        "authTokenField",
        "oAuthWebmethodVerbKey",
        "oAuthRequestBody",
        "webMethodsOAuth2RequestParams",
        "authURL",
        "headerKey"
      ];
      oAuthValidateFieldList.map((field) => ValidateField(field));
      const hasAuthErrors = oAuthValidateFieldList.map((fieldName) => {
        return typeof formRef?.current?.errors[fieldName] === "string";
      });
      if (hasAuthErrors.some((x) => x === true)) {
        setIsTestButtonLoading(false);
        AlertMessage({
          type: "warning",
          msg: "Authentication fields are required",
        });
        setTabValue(2);
        return;
      }

      const parseHeaders = (headersString) => {
        if (!headersString) return {};

        try {
          // Attempt to parse as JSON
          const headersObj = JSON.parse(headersString);
          if (typeof headersObj === "object" && headersObj !== null) {
            return headersObj;
          }
        } catch (e) {
          // Ignore JSON parse error and proceed with line-by-line parsing
        }

        const headers = {};
        const lines = headersString.split("\n");
        lines.forEach((line) => {
          const [key, value] = line.split(":").map((part) => part.trim());
          if (key && value) {
            headers[key] = value;
          }
        });

        return headers;
      };

      const headers = parseHeaders(formData.headers);
      const oAuthHeaders = parseHeaders(formData.oAuthHeaders);

      const requestObject = {
        webMethodURL: formData.webMethodURL,
        authenticationType: webMethodAuthTypeMaster.find(
          (x) =>
            x.authenticationTypeMasterKey === formData?.webMethodAuthTypeKey
        )?.authenticationType,
        webMethodVerb: webMethodVerbMaster.find(
          (x) => x.webMethodVerbKey === formData.webMethodVerbKey
        ).webMethodVerb,
        contentType: contentTypeMaster.find((x) => x.contentTypeKey === formData.contentTypeKey)?.contentType || "",
        requestBodyDataType: formData.requestBodyDataType
          ? requestBodyDataTypeMaster.find(
            (x) => x.requestBodyDataTypeKey === formData.requestBodyDataType
          )?.requestBodyDataType
          : null,
        requestBody: formData?.requestBody,
        headers: headers,
        parameterSeparator: formData?.parameterSeparator,
        authURL: formData?.authURL,
        oAuthContentType: contentTypeMaster.find((x) => x.contentTypeKey === formData.oAuthContentType)?.contentType || "",
        oAuthRequestBodyDataType: formData?.oAuthRequestBodyDataType
          ? requestBodyDataTypeMaster.find(
            (x) =>
              x.requestBodyDataTypeKey === formData.oAuthRequestBodyDataType
          )?.requestBodyDataType
          : null,
        oAuthHeaders: oAuthHeaders,
        headerKey: formData?.headerKey,
        oAuthWebmethodVerb: formData?.oAuthWebmethodVerbKey
          ? webMethodVerbMaster.find(
            (x) => x.webMethodVerbKey === formData.oAuthWebmethodVerbKey
          ).webMethodVerb
          : null,
        authTokenField: formData?.authTokenField,
        responseBody: formData?.responseBody,
        oAuthRequestBody: formData?.oAuthRequestBody,
        webMethodsRequestParams: formData?.webMethodsRequestParams,
        webMethodsOAuth2RequestParams: formData?.webMethodsOAuth2RequestParams,
      };

      const res = await executeWebMethod(requestObject);
      setIsTestButtonLoading(false);

      if (res.authResponse) {
        if (res.authResponse.statusCode === 200) {
          const authResponse = JSON.parse(res.authResponse.responseBody);
          const formattedAuthResponse = authResponseData(authResponse);
          formRef.current.setFieldValue("oAuth2ResponseFields", formattedAuthResponse);
          AlertMessage({ type: "success", msg: "Auth response fields updated successfully" });
          setTabValue(2);
        } else if (res.authResponse.statusCode === 415) {
          formRef.current.setFieldError("oAuthContentType", res.authResponse.UIMessage || "The OAuth Content Type format is incorrect");
          AlertMessage({ type: "error", msg: res.authResponse.UIMessage || "The OAuth Content Type format is incorrect." });
        } else {
          AlertMessage({ type: "error", msg: res.authResponse.responseBody });
        }
      }
      if (res.targetResponse) {
        if (res.targetResponse.statusCode === 200) {
          formRef.current.setFieldValue("responseBody", JSON.stringify(JSON.parse(res.targetResponse.responseBody), null, 2));
          AlertMessage({ type: "success", msg: "Response body updated successfully" });
        } else if (res.targetResponse.statusCode === 415) {
          formRef.current.setFieldError("contentTypeKey", res.targetResponse.UIMessage || "The Content Type format is incorrect");
          AlertMessage({ type: "error", msg: res.targetResponse.UIMessage || "The Content Type format is incorrect." });
        } else {
          AlertMessage({ type: "error", msg: res.targetResponse.responseBody });
        }
      }
      setIsTestButtonLoading(false);
    } catch (err) {
      setIsTestButtonLoading(false);
    }
  };

  const parseUrlParams = (url) => {
    const params = new URLSearchParams(url);
    const queryParams = [];
    if (params.size === 0) {
      queryParams.push({ parameterKey: "", parameterValue: "" });
    }
    for (const [key, value] of params.entries()) {
      queryParams.push({ parameterKey: key, parameterValue: value });
    }
    return queryParams;
  };
  const handleUrlChange = (event) => {
    const { name, value } = event.target;
    formRef.current.setFieldValue(name, value);
    formRef.current.setFieldValue("responseBody", "");

    const queryIndex = value.indexOf("?");
    if (queryIndex !== -1) {
      const splitString = value.substring(queryIndex + 1);
      if (splitString.length > 0) {
        setIsQueryTabDisabled(false);
        const contentType = contentTypeMaster?.find(
          (x) => x.contentType.toLowerCase() === "uri"
        )?.contentTypeKey;
        formRef.current.setFieldValue("contentTypeKey", contentType);
        setTabValue(1);
        formRef.current.setFieldValue(
          "webMethodsRequestParams",
          parseUrlParams(splitString)
        );
      } else {
        setTabValue(0);
        setIsQueryTabDisabled(true);
        formRef.current.setFieldValue("webMethodsRequestParams", []);
        formRef.current.setFieldValue("contentTypeKey", null);
        formRef.current.setFieldValue("requestBodyDataTypeKey", null);
      }
    } else {
      setTabValue(0);
      setIsQueryTabDisabled(true);
      formRef.current.setFieldValue("webMethodsRequestParams", []);
      formRef.current.setFieldValue("contentTypeKey", null);
      formRef.current.setFieldValue("requestBodyDataTypeKey", null);
    }
  };

  const handleAuthTypeChange = (value) => {
    formRef.current.setFieldValue(
      "webMethodAuthTypeKey",
      value?.authenticationTypeMasterKey
    );
    if (
      webMethodAuthTypeMaster?.find(
        (x) =>
          x.authenticationTypeMasterKey === value?.authenticationTypeMasterKey
      )?.authenticationType !== "OAuth 2.0"
    ) {
      setIsAuthTabDisabled(true);
      formRef.current.setFieldValue("oAuthWebmethodVerbKey", null);
      formRef.current.setFieldValue("authURL", null);
      formRef.current.setFieldValue("oAuthContentType", null);
      formRef.current.setFieldValue("oAuthRequestBodyDataType", null);
      formRef.current.setFieldValue("headerKey", null);
      formRef.current.setFieldValue("authTokenField", null);
      formRef.current.setFieldValue("oAuthHeaders", null);
      formRef.current.setFieldValue("webMethodsOAuth2RequestParams", [
        webMethodsOAuth2RequestParams,
      ]);
      formRef.current.setFieldValue("oAuth2ResponseFields", []);
      setTabValue(0);
    } else {
      setIsAuthTabDisabled(false);
    }
  };

  useEffect(() => {
    getWebMethodVerbMasterData();
    getContentTypeMasterData();
    getRequestBodyDataTypeMasterData();
    getWebMethodAuthTypeMasterrData();
    getFieldDataTypeMasterData();
    getFieldDataTypeFormatMasterData();
    if (webMethodKey) {
      fetchWebMethodsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValidJSON = (value: string | null): boolean => {
    if (value === null || value === undefined) return true;
    if (value.trim() === "" || value !== value.trim()) return false;
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== "object" || parsed === null) {
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const keyValueOrJsonValidator = (value) => {
    if (!value) return true; // Allow empty values

    const trimmedValue = value.trim();

    // Attempt to parse as JSON
    try {
      const parsed = JSON.parse(trimmedValue);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return true; // Valid JSON object
      }
    } catch (e) {
      // Ignore JSON parse error and proceed to key-value pair validation
    }

    // Check if it's key-value pairs each on a new line
    const lines = trimmedValue.split("\n");
    for (let line of lines) {
      const [key, val] = line.split(":").map((part) => part.trim());
      if (!key || !val) {
        return false; // If any line is not a valid key-value pair, return false
      }
    }

    return true; // Valid key-value pairs format
  };

  const headersValidation = Yup.string()
    .nullable()
    .max(4000, 'Headers cannot be more than 4000 characters long')
    .test(
      "headers-format",
      "Headers must be a valid JSON object or key-value pairs each on a new line",
      (value) => keyValueOrJsonValidator(value)
    );

  const oAuthHeadersValidation = Yup.string()
    .nullable()
    .test(
      "headers-format",
      "Headers must be a valid JSON object or key-value pairs each on a new line",
      (value) => keyValueOrJsonValidator(value)
    )
    .test(
      "headers-no-leading-trailing-spaces",
      "Headers should not contain leading or trailing spaces",
      (value) => {
        if (!value) return true;
        return value.trim() === value;
      }
    );

  return (
    <>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              alignItems="center"
              sx={{ my: 2 }}
              container
              justifyContent="space-between"
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Web Methods
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid alignItems="center" container spacing={2}>
                  <Grid item>
                    <Button
                      component={RouterLink}
                      to="/webMethods"
                      sx={{
                        background:
                          "linear-gradient(to bottom, #939ca3, #939ca3)",
                        minWidth: 100,
                      }}
                      variant="contained"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      sx={{
                        background:
                          "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100,
                      }}
                      component={RouterLink}
                      to=""
                      variant="contained"
                      onClick={submitForm}
                    >
                      Save
                      {isLoading && (
                        <CircularProgress
                          color="inherit"
                          thickness={5}
                          size={20}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CardContent>
              {isFetching && <CustomeSkeleton skeletonType="FormEdit" />}
              <Box sx={{ display: isFetching ? "none" : "" }}>
                <FormikForm
                  innerRef={formRef}
                  initialValues={defaultForm}
                  onSubmit={async (values): Promise<void> => {
                    setIsLoading(true);
                    const trimQueryParameters = (url: string) => {
                      const [baseUrl] = url.split("?");
                      return baseUrl;
                    };
                    const trimmedWebMethodURL = trimQueryParameters(values.webMethodURL);
                    const trimmedAuthURL = values.authURL ? trimQueryParameters(values.authURL) : "";

                    const selectedFields = values.oAuth2ResponseFields.filter(
                      (field) => field.save
                    );

                    await insertUpdateWebMethods({
                      ...values,
                      webMethodURL: trimmedWebMethodURL,
                      authURL: trimmedAuthURL,
                      oAuth2ResponseFields: selectedFields,
                    })
                      .then(() => {
                        setIsLoading(false);
                        const msg = values.isEdit
                          ? "Web Method updated successfully"
                          : "Web Method inserted successfully";
                        AlertMessage({ type: "success", msg });
                        navigate("/webMethods");
                      })
                      .catch((err) => {
                        setIsLoading(false);
                        if (err && err.errors) {
                          Object.entries(err.errors).forEach(
                            ([, errMsg]: any) => {
                              AlertMessage({
                                type: "error",
                                msg: errMsg.errorMessage,
                              });
                            }
                          );
                        } else {
                          AlertMessage({ type: "error", msg: err.title });
                        }
                        return false;
                      });
                  }}
                >
                  <FormikStep
                    validationSchema={Yup.object({
                      webMethodName: Yup.string()
                        .max(
                          100,
                          "Web Method Name cannot exceed 100 characters"
                        )
                        .required("Web Method Name is required")
                        .matches(
                          /^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/,
                          "Please enter valid Web Method Name"
                        ),
                      webMethodURL: Yup.string()
                        .required("URL is required")
                        .max(500, "URL cannot exceed 500 characters")
                        .test(
                          "url-noends-with",
                          "Must be a valid URL",
                          (value) => {
                            if (value && (value.endsWith("&") || value.endsWith("/"))) return false;
                            return true;
                          }
                        )
                        .url("Must be a valid URL"),
                      webMethodAuthTypeKey: Yup.string()
                        .nullable()
                        .required("Authentication Type is required"),
                      webMethodVerbKey: Yup.string()
                        .nullable()
                        .required("Verb is required"),
                      contentTypeKey: Yup.string()
                        .nullable()
                        .required("Content Type is required"),
                      requestBodyDataTypeKey: Yup.string()
                        .nullable()
                        .when("contentTypeKey", {
                          is: (value) => {
                            const contentType = contentTypeMaster.find(
                              (x) => x.contentTypeKey === value
                            );
                            return (
                              contentType && contentType.contentType === "raw"
                            );
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Request Body Data Type is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                      requestBody: Yup.string()
                        .nullable()
                        .when("contentTypeKey", {
                          is: (value) => {
                            const contentType = contentTypeMaster.find(
                              (x) => x.contentTypeKey === value
                            );
                            return (
                              contentType && contentType.contentType === "raw"
                            );
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Request Body is required")
                            .test(
                              "isValidJSON",
                              "Request Body must be a valid JSON",
                              isValidJSON
                            ),
                          otherwise: Yup.string().nullable(),
                        }),
                      parameterSeparator: Yup.string()
                        .nullable()
                        .max(
                          1,
                          "Parameter Separator cannot be more than one character"
                        )
                        .matches(
                          /^[&|,;]$/,
                          "Parameter Separator must be one of the following: & | , ;"
                        ),
                      webMethodsRequestParams: Yup.array().of(
                        Yup.object().shape({
                          parameterKey: Yup.string()
                            .matches(
                              /^[^\s]+$/,
                              "Parameter Key should not contain spaces"
                            )
                            .max(
                              500,
                              "Parameter Key cannot exceed 500 characters"
                            )
                            .matches(
                              /^(?!^0+$)[a-zA-Z0-9-~_]+(?: [a-zA-Z0-9-~_]+)*$/,
                              "Please enter valid Parameter Key"
                            )
                            .when("$contentTypeKey", {
                              is: (contentTypeKey) => {
                                const contentType = contentTypeMaster.find(
                                  (x) => x.contentTypeKey === contentTypeKey
                                );
                                return (
                                  contentType &&
                                  ["uri", "x-www-form-urlencoded"].includes(
                                    contentType.contentType
                                  )
                                );
                              },
                              then: Yup.string()
                                .nullable()
                                .required("Parameter Key is required"),
                              otherwise: Yup.string().nullable(),
                            }),
                          parameterValue: Yup.string()
                            .max(
                              500,
                              "Parameter Value cannot exceed 500 characters"
                            )
                            .matches(
                              /^[^\s]+$/,
                              "Parameter Value should not contain spaces"
                            )
                            .matches(
                              /^(?!^0+$)[a-zA-Z0-9-$]+(?: [a-zA-Z0-9-$]+)*$/,
                              "Please enter valid Parameter Value"
                            )
                            .nullable(),
                          fieldType: Yup.string()
                            .nullable(),
                          format: Yup.string()
                            .nullable(),
                        })
                      ),
                      oAuth2ResponseFields: Yup.array().when(
                        ["$oAuth2ResponseFields"],
                        {
                          is: (oAuth2ResponseFields) =>
                            oAuth2ResponseFields &&
                            oAuth2ResponseFields.length > 0,
                          then: Yup.array().of(
                            Yup.object().shape({
                              save: Yup.boolean(),
                              fieldName: Yup.string().when("save", {
                                is: true,
                                then: Yup.string()
                                  .nullable()
                                  .required("Field Name is required"),
                                otherwise: Yup.string().nullable(),
                              }),
                              displayName: Yup.string().when("save", {
                                is: true,
                                then: Yup.string().nullable(),
                                otherwise: Yup.string().nullable(),
                              }),
                              fieldType: Yup.string().when("save", {
                                is: true,
                                then: Yup.string()
                                  .nullable()
                                  .required("Field Type is required"),
                                otherwise: Yup.string().nullable(),
                              }),
                              format: Yup.string().when("save", {
                                is: true,
                                then: Yup.string().nullable(),
                                otherwise: Yup.string().nullable(),
                              }),
                            })
                          ),
                          otherwise: Yup.array(),
                        }
                      ),
                      webMethodsOAuth2RequestParams: Yup.array().of(
                        Yup.object().shape({
                          paramsKey: Yup.string().when("$oAuthContentType", {
                            is: (oAuthContentType) => {
                              const contentType = contentTypeMaster.find(
                                (x) => x.contentTypeKey === oAuthContentType
                              );
                              return (
                                contentType &&
                                ["uri", "x-www-form-urlencoded"].includes(
                                  contentType.contentType
                                )
                              );
                            },
                            then: Yup.string()
                              .nullable()
                              .required("Params Key is required"),
                            otherwise: Yup.string().nullable(),
                          }),
                          paramsValue: Yup.string().when("$oAuthContentType", {
                            is: (oAuthContentType) => {
                              const contentType = contentTypeMaster.find(
                                (x) => x.contentTypeKey === oAuthContentType
                              );
                              return (
                                contentType &&
                                ["uri", "x-www-form-urlencoded"].includes(
                                  contentType.contentType
                                )
                              );
                            },
                            then: Yup.string()
                              .nullable()
                              .required("Params Value is required"),
                            otherwise: Yup.string().nullable(),
                          }),
                        })
                      ),
                      headers: headersValidation,
                      oAuthHeaders: oAuthHeadersValidation,
                      authURL: Yup.string()
                        .nullable()
                        .max(500, "URL cannot exceed 500 characters")
                        .test(
                          "url-noends-with",
                          "Must be a valid URL",
                          (value) => {
                            if (value && (value.endsWith("&") || value.endsWith("/"))) return false;
                            return true;
                          }
                        )
                        .url("Must be a valid URL")
                        .when("webMethodAuthTypeKey", {
                          is: (webMethodAuthTypeKey) => {
                            const authType = webMethodAuthTypeMaster.find(
                              (x) =>
                                x.authenticationTypeMasterKey ===
                                webMethodAuthTypeKey
                            )?.authenticationType;
                            return authType === "OAuth 2.0";
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Auth URL is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                      oAuthContentType: Yup.string()
                        .nullable()
                        .when("webMethodAuthTypeKey", {
                          is: (webMethodAuthTypeKey) => {
                            const authType = webMethodAuthTypeMaster.find(
                              (x) =>
                                x.authenticationTypeMasterKey ===
                                webMethodAuthTypeKey
                            )?.authenticationType;
                            return authType === "OAuth 2.0";
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Content Type is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                      oAuthRequestBodyDataType: Yup.string()
                        .nullable()
                        .when("oAuthContentType", {
                          is: (value) => {
                            const contentType = contentTypeMaster.find(
                              (x) => x.contentTypeKey === value
                            );
                            return (
                              contentType && contentType.contentType === "raw"
                            );
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Request Body Data Type is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                      oAuthRequestBody: Yup.string()
                        .nullable()
                        .when("oAuthContentType", {
                          is: (value) => {
                            const contentType = contentTypeMaster.find(
                              (x) => x.contentTypeKey === value
                            );
                            return (
                              contentType && contentType.contentType === "raw"
                            );
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Request Body is required")
                            .test(
                              "isValidJSON",
                              "Request Body must be a valid JSON",
                              isValidJSON
                            ),
                          otherwise: Yup.string().nullable(),
                        }),
                      responseBody: Yup.string()
                        .nullable()
                        .required("Response Body is required"),
                      authTokenField: Yup.string()
                        .max(
                          500,
                          "Auth Token Field cannot exceed 500 characters"
                        )
                        .matches(
                          /^(?!([a-zA-Z0-9-])\1+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*(?:\[\])?(?:\.[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*)?$/,
                          "Please enter valid Auth Token Field"
                        )
                        .test('no-space', 'Please enter valid Auth Token Field', (value) => { return !/\s/.test(value) })
                        .nullable()
                        .when("webMethodAuthTypeKey", {
                          is: (webMethodAuthTypeKey) => {
                            const authType = webMethodAuthTypeMaster.find(
                              (x) =>
                                x.authenticationTypeMasterKey ===
                                webMethodAuthTypeKey
                            )?.authenticationType;
                            return authType === "OAuth 2.0";
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Auth Token Field is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                      headerKey: Yup.string()
                        .nullable()
                        .max(500, "Header Key cannot exceed 500 characters")
                        .matches(
                          /^[^\s]+$/,
                          "Header Key should not contain spaces"
                        )
                        .matches(
                          /^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/,
                          "Please enter valid Header Key"
                        )
                        .when("webMethodAuthTypeKey", {
                          is: (webMethodAuthTypeKey) => {
                            const authType = webMethodAuthTypeMaster.find(
                              (x) =>
                                x.authenticationTypeMasterKey ===
                                webMethodAuthTypeKey
                            )?.authenticationType;
                            return authType === "OAuth 2.0";
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Header Key is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                      oAuthWebmethodVerbKey: Yup.string()
                        .nullable()
                        .when("webMethodAuthTypeKey", {
                          is: (webMethodAuthTypeKey) => {
                            const authType = webMethodAuthTypeMaster.find(
                              (x) =>
                                x.authenticationTypeMasterKey ===
                                webMethodAuthTypeKey
                            )?.authenticationType;
                            return authType === "OAuth 2.0";
                          },
                          then: Yup.string()
                            .nullable()
                            .required("Verb is required"),
                          otherwise: Yup.string().nullable(),
                        }),
                    })}
                  >
                    <Grid spacing={2} container justifyContent="center">
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <InputField
                          label="Web Method Name *"
                          name="webMethodName"
                        />
                      </Grid>
                      <Grid item xs={5} sm={2} md={2} lg={2} xl={2}>
                        <SelectField
                          label="Verb *"
                          name="webMethodVerbKey"
                          data={webMethodVerbMaster}
                          dataTextField="webMethodVerb"
                          dataValueField="webMethodVerbKey"
                        />
                      </Grid>
                      <Grid item xs={5} sm={9} md={9} lg={9} xl={9}>
                        <InputField
                          label="Web Method URL *"
                          name="webMethodURL"
                          onChange={handleUrlChange}
                        />
                      </Grid>
                      <Grid item xs={2} sm={1} md={1} lg={1} xl={1}>
                        <Button
                          sx={{
                            background:
                              "linear-gradient(to bottom, #072f40, #072f40)",
                            color: "white",
                            width: '100%',
                            minWidth: '100%',
                            marginTop: "8px",
                          }}
                          variant="contained"
                          type="button"
                          disabled={isTestButtonLoading || isLoading}
                          disableRipple
                          onClick={handleTestButtonClick}
                        >
                          Test
                          {isTestButtonLoading && (
                            <CircularProgress
                              color="secondary"
                              thickness={5}
                              size={20}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Button>
                      </Grid>
                      <Grid item xs={2} sm={3} md={3} lg={3} xl={3}>
                        <SelectField
                          label="Web Method Auth Type *"
                          name="webMethodAuthTypeKey"
                          data={webMethodAuthTypeMaster}
                          dataTextField="authenticationType"
                          dataValueField="authenticationTypeMasterKey"
                          onChange={handleAuthTypeChange}
                        />
                      </Grid>
                      <Grid item xs={8} sm={9} md={9} lg={9} xl={9}>
                        <InputField
                          label="Headers"
                          name="headers"
                          multiline
                          rows={4}
                        />
                      </Grid>
                    </Grid>
                    <Divider variant="fullWidth" sx={{ paddingTop: "15px" }} />
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ paddingTop: "15px" }}>
                      <AppBar position="static" color="default">
                        <Tabs
                          value={tabValue}
                          onChange={handleChange}
                          indicatorColor="primary"
                          textColor="primary"
                          variant="scrollable"
                          scrollButtons="auto"
                          aria-label="scrollable auto tabs example"
                        >
                          <Tab
                            label="Body"
                            classes={{ selected: classes.selectedTab }}
                          />
                          <Tab
                            label="Query"
                            classes={{ selected: classes.selectedTab }}
                            disabled={isQueryTabDisabled}
                          />
                          <Tab
                            label="Authorization"
                            classes={{ selected: classes.selectedTab }}
                            disabled={isAuthTabDisabled}
                          />
                        </Tabs>
                      </AppBar>
                      <RequestBody
                        value={tabValue}
                        contentTypeMaster={contentTypeMaster}
                        requestBodyDataTypeMaster={requestBodyDataTypeMaster}
                        formRef={formRef}
                        setIsQueryTabDisabled={setIsQueryTabDisabled}
                        isLoading={isLoading}
                      />
                      <Query
                        value={tabValue}
                        formRef={formRef}
                        fieldDataTypeMaster={fieldDataTypeMaster}
                        fieldDataTypeFormatMaster={fieldDataTypeFormatMaster}
                        isLoading={isLoading}
                      />
                      <ApiAuth
                        value={tabValue}
                        parameterList={formRef}
                        contentTypeMaster={contentTypeMaster}
                        requestBodyDataTypeMaster={requestBodyDataTypeMaster}
                        webMethodVerbMaster={webMethodVerbMaster}
                        fieldDataTypeMaster={fieldDataTypeMaster}
                        fieldDataTypeFormatMaster={fieldDataTypeFormatMaster}
                        fieldArrayRef={fieldArrayRef}
                      />
                    </Grid>
                    <Divider variant="fullWidth" sx={{ paddingTop: "15px" }} />
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ paddingTop: "15px" }}>
                      <InputField
                        label="Response Body *"
                        name="responseBody"
                        multiline
                        rows={10}
                        disabled
                      />
                    </Grid>
                  </FormikStep>
                </FormikForm>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing response body will remove any widget fields mapped to this
            API. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              minWidth: 100,
              marginTop: "2%",
              marginRight: "2%",
              color: "rgb(107, 119, 140)",
            }}
            variant="outlined"
            disabled={isLoading}
            onClick={() => setOpenDialog(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCloseDialogAndSubmit}
            variant="contained"
            sx={{
              background: "linear-gradient(to bottom, #072f40, #072f40)",
              minWidth: 100,
              marginTop: "2%",
              marginRight: "2%",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebMethodsCreateEdit;
